import type { SurveyDefinition, SurveyQuestion } from './types'

type LocalFormat = 'xlsx' | 'csv' | 'json' | 'txt'

const supported = /\.(xlsx|csv|json|txt)$/i

function clean(value: unknown) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object' && 'text' in (value as Record<string, unknown>)) return String((value as { text: unknown }).text).trim()
  if (typeof value === 'object' && 'result' in (value as Record<string, unknown>)) return String((value as { result: unknown }).result).trim()
  return String(value).trim()
}

function csvRows(text: string) {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '"' && quoted && text[index + 1] === '"') { cell += '"'; index += 1; continue }
    if (char === '"') { quoted = !quoted; continue }
    if (char === ',' && !quoted) { row.push(cell); cell = ''; continue }
    if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[index + 1] === '\n') index += 1
      row.push(cell); cell = ''
      if (row.some((value) => value.trim())) rows.push(row)
      row = []
      continue
    }
    cell += char
  }
  row.push(cell)
  if (row.some((value) => value.trim())) rows.push(row)
  return rows
}

function questionType(value: string, hasOptions: boolean): SurveyQuestion['type'] {
  const normalized = value.trim().toLowerCase()
  const types: Record<string, SurveyQuestion['type']> = {
    single: 'single', 单选: 'single', 单选题: 'single',
    multi: 'multi', 多选: 'multi', 多选题: 'multi',
    text: 'text', 文本: 'text', 填空: 'text', 开放题: 'text',
    number: 'number', 数字: 'number', date: 'date', 日期: 'date',
    scale: 'scale', 量表: 'scale', slider: 'slider', 滑块: 'slider', ranking: 'ranking', 排序: 'ranking',
  }
  return types[normalized] || (hasOptions ? 'single' : 'text')
}

function fromRows(rows: string[][], fileName: string, sheetName?: string) {
  const headers = (rows[0] || []).map((value) => clean(value).toLowerCase())
  const find = (...names: string[]) => headers.findIndex((header) => names.some((name) => header === name || header.includes(name)))
  const promptColumn = find('题目', '题干', '问题', 'question', 'prompt')
  const typeColumn = find('题型', '类型', 'type')
  const optionsColumn = find('选项', 'options', '答案选项')
  const requiredColumn = find('必填', '必答', 'required')
  const dimensionColumn = find('维度', 'dimension')
  const hintColumn = find('提示', '说明', 'hint')
  const minColumn = find('最小值', 'min')
  const maxColumn = find('最大值', 'max')
  const leftColumn = find('左标签', 'left')
  const rightColumn = find('右标签', 'right')
  const structured = promptColumn >= 0
  const sourceRows = structured ? rows.slice(1) : rows
  const questions: SurveyQuestion[] = []
  const mappings: NonNullable<SurveyDefinition['sourceImport']>['mappings'] = []

  sourceRows.forEach((raw, sourceIndex) => {
    const cells = raw.map(clean)
    const prompt = structured ? cells[promptColumn] : cells.find((value) => value.length >= 4) || ''
    if (!prompt || /^(题目|题干|问题|question)$/i.test(prompt)) return
    const optionText = structured && optionsColumn >= 0 ? cells[optionsColumn] : cells.slice(1).filter(Boolean).join('|')
    const labels = optionText.split(/[|｜;；]/).map((value) => value.trim()).filter(Boolean)
    const type = questionType(structured && typeColumn >= 0 ? cells[typeColumn] : '', labels.length > 1)
    const id = `q${questions.length + 1}`
    const question: SurveyQuestion = {
      id,
      type,
      prompt: prompt.slice(0, 500),
      required: structured && requiredColumn >= 0 ? !/^(否|false|0|no)$/i.test(cells[requiredColumn]) : true,
      dimension: structured && dimensionColumn >= 0 && cells[dimensionColumn] ? cells[dimensionColumn].slice(0, 100) : 'general',
    }
    if (structured && hintColumn >= 0 && cells[hintColumn]) question.hint = cells[hintColumn].slice(0, 500)
    if (['single', 'multi', 'ranking'].includes(type) && labels.length) question.options = labels.map((label, index) => ({ value: `o${index + 1}`, label: label.slice(0, 300) }))
    if (type === 'scale' || type === 'slider' || type === 'number') {
      const min = structured && minColumn >= 0 ? Number(cells[minColumn]) : 1
      const max = structured && maxColumn >= 0 ? Number(cells[maxColumn]) : type === 'slider' ? 100 : 5
      question.min = Number.isFinite(min) ? min : 1
      question.max = Number.isFinite(max) && max > question.min ? max : 5
      if (structured && leftColumn >= 0 && cells[leftColumn]) question.leftLabel = cells[leftColumn]
      if (structured && rightColumn >= 0 && cells[rightColumn]) question.rightLabel = cells[rightColumn]
    }
    questions.push(question)
    mappings.push({ questionId: id, sourceLabel: prompt.slice(0, 200), sourceIndex: sourceIndex + (structured ? 1 : 0), ...(sheetName ? { sourceSheet: sheetName, sourceRow: sourceIndex + (structured ? 2 : 1) } : {}) })
  })
  return { questions, mappings, structured }
}

function fromJson(value: unknown, fileName: string) {
  const root = value as Record<string, unknown>
  const source = Array.isArray(value) ? value : Array.isArray(root?.questions) ? root.questions : Array.isArray((root?.definition as Record<string, unknown>)?.questions) ? (root.definition as Record<string, unknown>).questions as unknown[] : []
  const rows = source.map((item) => {
    if (typeof item === 'string') return [item]
    const record = item as Record<string, unknown>
    const options = Array.isArray(record.options) ? record.options.map((option) => clean(typeof option === 'object' ? (option as Record<string, unknown>).label : option)).join('|') : ''
    return [clean(record.prompt || record.question || record.title), clean(record.type), options, clean(record.required), clean(record.dimension), clean(record.hint)]
  })
  return fromRows([['题目', '题型', '选项', '必填', '维度', '提示'], ...rows], fileName)
}

export async function analyzeLocalSurveyFile(file: File, mode: 'template' | 'ai' = 'template') {
  if (!supported.test(file.name)) throw new Error('本地模式支持 XLSX、CSV、JSON 与 TXT；Word、PDF 和 AI 语义解析需交由管理员处理。')
  if (file.size > 15 * 1024 * 1024) throw new Error('文件超过 15MB，请压缩或拆分后再导入。')
  const format = file.name.split('.').pop()!.toLowerCase() as LocalFormat
  let parsed: ReturnType<typeof fromRows>
  let parser = 'browser-text'
  if (format === 'xlsx') {
    const module = await import('exceljs')
    const workbook = new module.default.Workbook()
    await workbook.xlsx.load(await file.arrayBuffer() as unknown as ArrayBuffer)
    const sheet = workbook.getWorksheet('题目') || workbook.worksheets.find((item) => item.actualRowCount > 0)
    if (!sheet) throw new Error('Excel 文件中没有可读取的工作表。')
    const rows: string[][] = []
    sheet.eachRow({ includeEmpty: false }, (row) => {
      const values = Array.isArray(row.values) ? row.values.slice(1).map(clean) : []
      if (values.some(Boolean)) rows.push(values)
    })
    parsed = fromRows(rows, file.name, sheet.name)
    parser = 'browser-exceljs'
  } else if (format === 'json') {
    parsed = fromJson(JSON.parse(await file.text()), file.name)
    parser = 'browser-json'
  } else {
    const text = await file.text()
    parsed = format === 'csv' ? fromRows(csvRows(text), file.name) : fromRows(text.split(/\r?\n/).filter(Boolean).map((line) => [line]), file.name)
    parser = format === 'csv' ? 'browser-csv' : 'browser-text'
  }
  if (!parsed.questions.length) throw new Error('没有识别到可填写题目。请使用标准表头：题目、题型、选项、必填、维度。')
  const title = file.name.replace(/\.[^.]+$/, '').slice(0, 100)
  const warnings = [
    ...(parsed.structured ? [] : ['未识别到标准表头，已按每行一题进行基础整理，请逐题复核。']),
    ...(mode === 'ai' ? ['本地工具不会把内容发送给 AI；已改用浏览器内结构解析。'] : []),
  ]
  const definition: SurveyDefinition = {
    intro: `请根据“${title}”中的说明认真作答。`,
    consent: '继续填写表示你已阅读问卷说明并自愿参与；你可以随时退出。',
    completionMessage: '感谢你的认真回答。',
    dimensions: [{ key: 'general', label: '综合结果', color: '#718064' }],
    questions: parsed.questions,
    profiles: [],
    presentation: { mode: 'neutral', navigationMode: 'scroll', randomizeQuestions: false, questionsPerPage: 1, showProgress: true, positiveFeedback: false, resultMode: 'scores_only' },
    governance: { communityIntro: `由“${file.name}”在本地导入的问卷，公开前需要管理员复核。`, impactLevel: 1 },
    sourceImport: { fileName: file.name, format, mode: 'template', importedAt: new Date().toISOString(), parser, aiEnhanced: false, confidence: parsed.structured ? 'high' : 'medium', warnings, mappings: parsed.mappings },
  }
  return {
    source: { fileName: file.name, format, mode: 'template' as const, sizeBytes: file.size, parser, aiEnhanced: false, confidence: parsed.structured ? 'high' as const : 'medium' as const, warnings, canCreate: true, blockingIssues: [] },
    draft: { title, description: `由“${file.name}”在本地转换生成的可填写问卷。`, type: 'general' as const, category: '文件导入', riskLevel: 'low' as const, minAge: 12, accent: '#7b3445', estimatedMinutes: Math.max(1, Math.ceil(parsed.questions.length * .45)), definition, settings: { quota: null, startsAt: null, endsAt: null, responseLimit: 'once' as const, requireLogin: false, allowSaveProgress: true } },
  }
}
